import superjson from 'superjson'
import S from '@/settings'
import type { IServiceError } from '@/types/error'
import log from '@/providers/log'
import { HTTP_CLIENT_ERRORS } from '@/lib/constants/errors'
import {
  buildErrorResult,
  getErrorText,
  handleErrorResponse,
  logErrorResponseDetails,
  logNetworkErrorDetails,
  stripQuery,
} from '@/providers/httpClientErrors'

function getBaseURL(): string {
  if (typeof window !== 'undefined') {
    return ''
  }
  return S.BASE_URL.local
}

function buildFormDataBody(filesToUpload: FormData, attributes?: unknown, fileToDelete?: unknown): FormData {
  const body = new FormData()
  const files = filesToUpload.getAll('files') as File[]
  files.forEach((file) => {
    body.append('files', file)
  })

  if (attributes) {
    body.append('attributes', JSON.stringify(attributes))
  }

  if (fileToDelete) {
    body.append('fileToDelete', JSON.stringify(fileToDelete))
  }

  return body
}

function buildRequestBody(body: unknown, useRawJson = false): { body: BodyInit; headers: HeadersInit } {
  if (body instanceof FormData) {
    return {
      body,
      headers: {},
    }
  }

  if (body && typeof body === 'object' && 'filesToUpload' in body) {
    const { filesToUpload, attributes, fileToDelete } = body as {
      filesToUpload: FormData
      attributes?: unknown
      fileToDelete?: unknown
    }
    return {
      body: buildFormDataBody(filesToUpload, attributes, fileToDelete),
      headers: {},
    }
  }

  return {
    body: useRawJson ? JSON.stringify(body || {}) : superjson.stringify(body || {}),
    headers: { 'Content-Type': 'application/json' },
  }
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const text = await response.text()
  if (!text || text.trim() === '') {
    return undefined as T
  }
  const trimmed = text.trimStart()
  if (trimmed.startsWith('{"json"')) {
    return superjson.parse<T>(text)
  }
  return JSON.parse(text) as T
}

export interface IRequestOptions {
  headers?: Record<string, string>
  useRawJson?: boolean
  preserveFullError?: boolean
  shouldLogErrorDetails?: boolean
}

function isExternalUrl(url: string): boolean {
  return url.startsWith('http://') || url.startsWith('https://')
}

function buildFetchOptions(method: string, body?: unknown, options?: IRequestOptions): RequestInit {
  const fetchOptions: RequestInit = { method }
  if (body !== undefined) {
    const { body: requestBody, headers } = buildRequestBody(body, options?.useRawJson)
    fetchOptions.body = requestBody
    fetchOptions.headers = { ...headers, ...options?.headers }
  } else if (options?.headers) {
    fetchOptions.headers = options.headers
  }
  return fetchOptions
}

async function readSuccessResponse<T>(response: Response, label: string): Promise<T | IServiceError> {
  try {
    return await parseJsonResponse<T>(response)
  } catch (error) {
    // Typically an HTML login or proxy page served with a 2xx status
    log.error(`${label} returned an unparseable body: ${getErrorText(error)}`)
    return buildErrorResult(HTTP_CLIENT_ERRORS.UNEXPECTED_RESPONSE)
  }
}

async function makeRequest<T>(
  method: string,
  endpoint: string,
  body?: unknown,
  options?: IRequestOptions,
): Promise<T | IServiceError> {
  const label = `${method} ${stripQuery(endpoint)}`
  const url = isExternalUrl(endpoint) ? endpoint : `${getBaseURL()}${endpoint}`
  let response: Response
  try {
    response = await fetch(url, buildFetchOptions(method, body, options))
  } catch (error) {
    // The exception text can carry the upstream URL
    if (options?.shouldLogErrorDetails) logNetworkErrorDetails(method, url, error)
    else log.error(`${label} failed: ${getErrorText(error)}`)
    return buildErrorResult(HTTP_CLIENT_ERRORS.NETWORK)
  }
  if (!response.ok) {
    if (options?.shouldLogErrorDetails) await logErrorResponseDetails(method, response.clone())
    return handleErrorResponse(response, options?.preserveFullError)
  }
  return readSuccessResponse<T>(response, label)
}

export const httpClient = {
  get: <T>(endpoint: string, options?: IRequestOptions): Promise<T | IServiceError> => {
    return makeRequest<T>('GET', endpoint, undefined, options)
  },

  post: <T>(endpoint: string, body?: unknown, options?: IRequestOptions): Promise<T | IServiceError> => {
    return makeRequest<T>('POST', endpoint, body, options)
  },

  put: <T>(endpoint: string, body?: unknown, options?: IRequestOptions): Promise<T | IServiceError> => {
    return makeRequest<T>('PUT', endpoint, body, options)
  },

  patch: <T>(endpoint: string, body?: unknown, options?: IRequestOptions): Promise<T | IServiceError> => {
    return makeRequest<T>('PATCH', endpoint, body, options)
  },

  delete: <T>(endpoint: string, body?: unknown, options?: IRequestOptions): Promise<T | IServiceError> => {
    return makeRequest<T>('DELETE', endpoint, body, options)
  },
}
